import { z } from 'zod';

const MONEY_PATTERN = /^\d+(\.\d{1,4})?$/;
const MONEY_MESSAGE = 'Must be 0 or more with up to 4 decimal places';

export const BandRow = z.object({
    band_index: z.number().int().min(1).max(10),
    cost_net:   z.preprocess(
        (v) => (v === '' || v === undefined ? null : v),
        z.union([z.null(), z.string().regex(MONEY_PATTERN, MONEY_MESSAGE)]),
    ),
    cost_gross: z.preprocess(
        (v) => (v === '' || v === undefined ? null : v),
        z.union([z.null(), z.string().regex(MONEY_PATTERN, MONEY_MESSAGE)]),
    ),
    percentage: z.preprocess(
        (v) => (v === '' || v === undefined ? null : v),
        z.union([z.null(), z.string().regex(/^\d+(\.\d{1,4})?$/, 'Must be a percentage with up to 4 decimal places')]),
    ),
});

const NullableMoney = z.preprocess(
    (v) => (v === '' || v === undefined ? null : v),
    z.union([z.null(), z.string().regex(MONEY_PATTERN, MONEY_MESSAGE)]),
);

const NullablePercentage = z.preprocess(
    (v) => (v === '' || v === undefined ? null : v),
    z.union([z.null(), z.string().regex(/^\d+(\.\d{1,4})?$/, 'Must be a percentage with up to 4 decimal places')]),
);

export const productFeeSchema = z.object({
    fee_id:                z.string().uuid('Select a fee'),
    value_type_id:         z.string().uuid('Select a value type'),
    scheduling_method_id:  z.string().uuid('Select a scheduling method'),
    paid_by_id:            z.string().uuid('Select who pays'),
    paid_to_id:            z.string().uuid('Select who is paid'),
    vat_category_id:       z.string().uuid('Select a VAT category'),
    applies_to_ids:        z.array(z.string().uuid()).min(1, 'Select at least one quote type'),
    application_scope:     z.enum(['per_plan', 'per_service']),
    is_absorbed:           z.boolean(),
    cap_value_gross:       NullableMoney,
    default_cost_net:      NullableMoney,
    default_cost_gross:    NullableMoney,
    default_percentage:    NullablePercentage,
    display_order:         z.number().int().min(0).optional(),
    bands:                 z.array(BandRow).max(10).optional(),
});

export type ProductFeeFormInput = z.input<typeof productFeeSchema>;
export type ProductFeeFormOutput = z.output<ReturnType<typeof makeProductFeeSchema>>;

export function makeProductFeeSchema(isPercentageType: boolean, requiresLookup: boolean = false) {
    return productFeeSchema.superRefine((data, ctx) => {
        const hasPercentage = data.default_percentage !== null && data.default_percentage !== undefined;
        const hasNet        = data.default_cost_net !== null && data.default_cost_net !== undefined;

        if (!isPercentageType && data.cap_value_gross !== null && data.cap_value_gross !== undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['cap_value_gross'],
                message: 'A cap is only valid for percentage value types.',
            });
        }

        if (isPercentageType) {
            if (!hasPercentage) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['default_percentage'],
                    message: 'A percentage value is required for this value type.',
                });
            }
            if (hasNet) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['default_cost_net'],
                    message: 'Net amount must be empty for percentage value types.',
                });
            }
        } else {
            if (!hasNet) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['default_cost_net'],
                    message: 'A net amount is required for flat value types.',
                });
            }
            if (hasPercentage) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['default_percentage'],
                    message: 'Percentage must be empty for flat value types.',
                });
            }
        }

        const bands = data.bands ?? [];

        if (!requiresLookup && bands.length > 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['bands'],
                message: 'Bands are only valid for lookup value types.',
            });
        }

        const seen = new Set<number>();
        bands.forEach((b, i) => {
            if (seen.has(b.band_index)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['bands', i, 'band_index'],
                    message: 'Band index must be unique.',
                });
            }
            seen.add(b.band_index);
        });

        if (requiresLookup) {
            bands.forEach((b, i) => {
                const hasBandNet   = b.cost_net != null;
                const hasBandPct   = b.percentage != null;

                if (isPercentageType) {
                    if (!hasBandPct) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            path: ['bands', i, 'percentage'],
                            message: 'A percentage is required for this band.',
                        });
                    }
                    if (hasBandNet) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            path: ['bands', i, 'cost_net'],
                            message: 'Net must be empty for percentage lookup bands.',
                        });
                    }
                } else {
                    if (!hasBandNet) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            path: ['bands', i, 'cost_net'],
                            message: 'A net cost is required for this band.',
                        });
                    }
                    if (hasBandPct) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            path: ['bands', i, 'percentage'],
                            message: 'Percentage must be empty for value lookup bands.',
                        });
                    }
                }
            });
        }
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
    }).transform(({ default_cost_gross: _defaultCostGrossOmit, bands, ...rest }) => ({
        ...rest,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        bands: bands?.map(({ cost_gross: _bandCostGrossOmit, ...bandRest }) => bandRest),
    }));
}
